import { ChangeEvent, Dispatch, ReactNode, SetStateAction } from "react";
import { Col, Label, Row } from "reactstrap";

import ActionBar from "../../../common/ActionBar/ActionBar";
import PageHeader from "../../../common/PageHeader/PageHeader";
import { Dropdown, TextField } from "../../../common/VFX-UI";
import NumericTextField from "../../../common/VFX-UI/TextField/NumericTextField";
import { getFormattedDate } from "../../../../utility/helpers";
import { IsMobile } from "../../../../utility/lib/breakpoints";
import {
  privacyOptions,
  TAX_TEXT_STYLE,
  TAX_TEXT_STYLE_MOBILE,
} from "../constants";
import customImageButton from "../../../../assets/images/Donate_Button.png";
import { OrganizationEmployeeDto } from "../../../../app/model/api/types/dto";
import { OrganizationCampaignDto } from "../../../../app/model/api/types/dto/organizationCampaignDto";
import usePledgeStyles from "../styles";

type PledgeStyles = ReturnType<typeof usePledgeStyles>;

interface PledgeHeaderProps {
  employee?: OrganizationEmployeeDto;
  campaign?: OrganizationCampaignDto;
  styles: PledgeStyles;
}

interface SpouseSectionProps {
  show: boolean;
  showDetails: boolean;
  setShowDetails: Dispatch<SetStateAction<boolean>>;
  setFieldValue: (field: string, value: unknown) => Promise<unknown>;
  styles: PledgeStyles;
}

interface PrivacySectionProps {
  show: boolean;
  values: { isWillingToStayAnonymous: string | null };
  styles: PledgeStyles;
}

interface PledgeActionValues {
  isNotWillingToContribute: boolean | null;
}

interface PledgeActionsProps {
  values: PledgeActionValues;
  isValid: boolean;
  dirty: boolean;
  isDesktop: boolean;
  status: string;
  displayText: ReactNode;
  saveInput: () => void;
  handleOnSubmit: (values: PledgeActionValues) => void | Promise<void>;
  styles: PledgeStyles;
}

export const PledgeHeader = (props: PledgeHeaderProps) => (
  <PageHeader>
    <Row className="border-bottom mb-3 pb-2">
      <Col xs={12}>
        <span>
          {[props.employee?.organization, props.campaign?.name]
            .filter((value) => Boolean(value?.trim()))
            .join(" : ")}
        </span>
      </Col>
      <Col xs={12} sm={6}>
        <span className="float-start" style={props.styles.pageHeaderSubtext}>
          {getFormattedDate(props.campaign?.startDate) + " - " +
            getFormattedDate(props.campaign?.endDate)}{" "}
        </span>
      </Col>
      <Col style={{ marginTop: "auto" }} xs={12} sm={6}>
        <span style={props.styles.pageHeaderName} className="float-end">
          {[
            props.employee?.personnelNumber?.trim()
              ? `${props.employee.personnelNumber}:`
              : null,
            [
              props.employee?.firstName,
              props.employee?.mi,
              props.employee?.lastName,
            ]
              .filter((value) => Boolean(value?.trim()))
              .join(" "),
          ]
            .filter(Boolean)
            .join(" ")}
        </span>
      </Col>
    </Row>
  </PageHeader>
);

export const SpouseSection = (props: SpouseSectionProps) => {
  if (!props.show) return null;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked || event.target.value === "1";
    props.setShowDetails(enabled);
    if (!enabled) {
      props.setFieldValue("spouseName", null);
      props.setFieldValue("spouseWorkPlace", null);
      props.setFieldValue("spousePledgeAmount", null);
    }
  };

  return (
    <Row>
      <Label style={props.styles.sectionHeader} className="border-bottom mb-3 pb-2">
        I would like to combine my giving with my spouse&apos;s giving to United
        Way, if applicable.
      </Label>
      <Col xs={4}>
        <Dropdown
          options={privacyOptions}
          name="isWillingToCombineSpouseContribution"
          onChange={handleChange}
        />
      </Col>
      {props.showDetails && (
        <div className="ms-3">
          <div className="row">
            <div className="col-6">
              <TextField name="spouseName" label="Name" type="text" />
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <TextField name="spouseWorkPlace" label="Work Place" type="text" />
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <NumericTextField
                isDollar
                decimalPlace={0}
                name="spousePledgeAmount"
                label="Amount"
              />
            </div>
          </div>
        </div>
      )}
    </Row>
  );
};

export const PrivacySection = (props: PrivacySectionProps) => {
  if (!props.show) return null;
  return (
    <Row>
      <Label style={props.styles.sectionHeader} className="border-bottom mb-3 pb-2">
        Privacy
      </Label>
      <div>Do you wish to be anonymous?</div>
      <Col xs={4}>
        <Dropdown options={privacyOptions} name="isWillingToStayAnonymous" />
      </Col>
      <div className="mb-3 pb-2 ms-3">
        {props.values.isWillingToStayAnonymous === "1" && (
          <i>
            You will <u>NOT</u> be included in ExxonMobil drawings and events
            and your name will <u>NOT</u> appear via company/Capital Area United
            Way media
          </i>
        )}
        {props.values.isWillingToStayAnonymous === "2" && (
          <i>
            You will be included in ExxonMobil drawings and events and your name
            will appear via company/Capital Area United Way media
          </i>
        )}
      </div>
    </Row>
  );
};

export const PledgeActions = (props: PledgeActionsProps) => (
  <Row className="mt-4">
    <Label style={props.styles.sectionHeader} className="mb-0 pb-2">4. Review and submit</Label>
    <ActionBar
      submitDisabled={
        props.values.isNotWillingToContribute ? false : !props.isValid || !props.dirty
      }
      displayTextStyle={props.isDesktop ? TAX_TEXT_STYLE : TAX_TEXT_STYLE_MOBILE}
      displayText={props.displayText}
      submitHandler={() =>
        props.values.isNotWillingToContribute
          ? props.handleOnSubmit(props.values)
          : props.saveInput()
      }
      isLoading={props.status === "pending"}
      customImageButton={customImageButton}
    />
    {IsMobile() && (
      <div
        style={{
          ...TAX_TEXT_STYLE,
          paddingRight: "0%!important",
          marginTop: -30,
        }}
      >
        {props.displayText}
      </div>
    )}
  </Row>
);
